import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

interface Tenant {
  id: string;
  name: string;
  code: string;
}

interface TenantMembership {
  tenant_id: string;
  role: string;
  dashboard_access: string[];
  tenant: Tenant;
}

interface TenantContextType {
  tenants: Tenant[];
  currentTenant: Tenant | null;
  currentRole: string | null;
  dashboardAccess: string[];
  setCurrentTenant: (tenant: Tenant) => void;
  loading: boolean;
  isSuperAdmin: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

function mapTenant(row: any): Tenant {
  return { id: row.id, name: row.tenant_name, code: row.tenant_code };
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [currentRole, setCurrentRole] = useState<string | null>(null);
  const [dashboardAccess, setDashboardAccess] = useState<string[]>([]);
  const [memberships, setMemberships] = useState<TenantMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    if (!user) {
      setTenants([]);
      setCurrentTenant(null);
      setCurrentRole(null);
      setIsSuperAdmin(false);
      setLoading(false);
      return;
    }

    const fetchMemberships = async () => {
      setLoading(true);

      // Fetch user_profiles to check role and tenant_id
      const { data: profile } = await supabase
        .from("user_profiles" as any)
        .select("role, tenant_id")
        .eq("id", user.id)
        .maybeSingle() as any;

      const userRole = profile?.role;
      const profileTenantId = profile?.tenant_id;
      const superAdmin = userRole === "super_admin";
      setIsSuperAdmin(superAdmin);

      // For non-super-admin users, lock to their user_profiles.tenant_id
      if (!superAdmin && profileTenantId) {
        const { data: tenantData } = await supabase
          .from("tenants")
          .select("*")
          .eq("id", profileTenantId);

        const mapped = (tenantData ?? []).map(mapTenant);
        if (mapped.length > 0) {
          setTenants(mapped);
          setCurrentTenant(mapped[0]);
        }

        const { data: memData } = await supabase
          .from("tenant_memberships")
          .select("tenant_id, role, dashboard_access")
          .eq("user_id", user.id)
          .eq("tenant_id", profileTenantId);

        if (memData && memData.length > 0) {
          setCurrentRole(memData[0].role);
          setDashboardAccess(memData[0].dashboard_access ?? []);
          const mems = memData.map((m) => ({
            ...m,
            tenant: mapped.find((t) => t.id === m.tenant_id)!,
          }));
          setMemberships(mems);
        }
        setLoading(false);
        return;
      }

      // Super admin or fallback: fetch all memberships
      const { data, error } = await supabase
        .from("tenant_memberships")
        .select("tenant_id, role, dashboard_access")
        .eq("user_id", user.id);

      // Super admin: always load ALL tenants (regardless of memberships)
      if (superAdmin) {
        const { data: allTenants } = await supabase.from("tenants").select("*");
        if (allTenants && allTenants.length > 0) {
          const mapped = allTenants.map(mapTenant);
          setTenants(mapped);
          const allAccess = ["survey","valve_assessment","dma_zero_pressure_test","dma_boundary_valve_status","chamber_construction","valve_repair","gis_dashboard"];
          const syntheticMems = mapped.map((t) => ({
            tenant_id: t.id,
            role: "admin",
            dashboard_access: allAccess,
            tenant: t,
          }));
          setMemberships(syntheticMems);
          const prev = currentTenant ? mapped.find((t) => t.id === currentTenant.id) : null;
          setCurrentTenant(prev ?? mapped[0]);
          setCurrentRole("admin");
          setDashboardAccess(allAccess);
        }
        setLoading(false);
        return;
      }

      if (error || !data || data.length === 0) {
        setLoading(false);
        return;
      }

      const tenantIds = data.map((m) => m.tenant_id);
      const { data: tenantData } = await supabase
        .from("tenants")
        .select("*")
        .in("id", tenantIds);

      if (tenantData) {
        const mapped = tenantData.map(mapTenant);
        setTenants(mapped);
        const mems = data.map((m) => ({
          ...m,
          tenant: mapped.find((t) => t.id === m.tenant_id)!,
        }));
        setMemberships(mems);

        if (mapped.length > 0) {
          const savedTenantId = currentTenant?.id;
          const matchIndex = savedTenantId ? mapped.findIndex(t => t.id === savedTenantId) : -1;
          const idx = matchIndex >= 0 ? matchIndex : 0;
          setCurrentTenant(mapped[idx]);
          setCurrentRole(data[idx].role);
          setDashboardAccess(data[idx].dashboard_access ?? []);
        }
      }
      setLoading(false);
    };

    fetchMemberships();
  }, [user]);

  const handleSetTenant = (tenant: Tenant) => {
    if (!isSuperAdmin) return; // tenant admins cannot switch
    setCurrentTenant(tenant);
    const mem = memberships.find((m) => m.tenant_id === tenant.id);
    setCurrentRole(mem?.role ?? null);
    setDashboardAccess(mem?.dashboard_access ?? []);
  };

  return (
    <TenantContext.Provider value={{ tenants, currentTenant, currentRole, dashboardAccess, setCurrentTenant: handleSetTenant, loading, isSuperAdmin }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) throw new Error("useTenant must be used within TenantProvider");
  return context;
}
